import { parseISO, startOfISOWeek, differenceInCalendarWeeks } from 'date-fns';

// Choices a manager picks when adding a per-client checklist task.
// "daily" = every visit (the original behavior, used as the default for
// pre-existing items that have no frequency stored).
export const CHECKLIST_FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Every visit', shortLabel: 'Every visit' },
  { value: 'weekly', label: 'Weekly', shortLabel: 'Weekly' },
  { value: 'biweekly', label: 'Bi-weekly', shortLabel: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly', shortLabel: 'Monthly' },
];

export const CHECKLIST_FREQUENCY_LABELS = CHECKLIST_FREQUENCY_OPTIONS.reduce(
  (acc, o) => { acc[o.value] = o.shortLabel; return acc; },
  {}
);

// Fixed anchor so bi-weekly buckets are stable across renders / devices.
// Monday, Jan 1 2024.
const BIWEEKLY_EPOCH = new Date(2024, 0, 1);

const safeParseDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string') {
    try { return parseISO(value); } catch { return null; }
  }
  if (typeof value?.toDate === 'function') return value.toDate();
  return null;
};

// Stable sort key so two jobs on the same day pick a deterministic anchor.
const jobOrderKey = (job) => {
  const date = job.date || '9999-12-31';
  const time = job.scheduledTime || '00:00';
  const id = job.id || '';
  return `${date}T${time}#${id}`;
};

const isJobActive = (job) => {
  const status = (job.status || '').toLowerCase();
  return status !== 'cancelled' && status !== 'canceled';
};

const periodKeyFor = (date, frequency) => {
  if (frequency === 'weekly') {
    const weekStart = startOfISOWeek(date);
    return `w-${weekStart.toISOString().slice(0, 10)}`;
  }
  if (frequency === 'biweekly') {
    const weeks = differenceInCalendarWeeks(date, BIWEEKLY_EPOCH, { weekStartsOn: 1 });
    return `bw-${Math.floor(weeks / 2)}`;
  }
  if (frequency === 'monthly') {
    return `m-${date.getFullYear()}-${date.getMonth()}`;
  }
  return 'daily';
};

// For a non-daily checklist item, the task appears on exactly ONE visit per
// period (week / two weeks / month). That anchor visit is the earliest active
// job for the client in the period — which means a shelter cleaned Mon/Wed/Fri
// gets its weekly tasks on Monday, its monthly tasks on the first Monday of
// the month, and its daily tasks on all three days.
export function getApplicableChecklistItems(items, job, clientJobs) {
  if (!items || items.length === 0) return [];
  if (!job) return items;

  const jobDate = safeParseDate(job.date);
  // If the target job has no date we can't bucket it — only daily items apply.
  if (!jobDate) {
    return items.filter((item) => (item.frequency || 'daily') === 'daily');
  }

  const activeJobs = (clientJobs || []).filter(isJobActive);

  const earliestByPeriod = {
    weekly: new Map(),
    biweekly: new Map(),
    monthly: new Map(),
  };

  for (const otherJob of activeJobs) {
    const otherDate = safeParseDate(otherJob.date);
    if (!otherDate) continue;
    const orderKey = jobOrderKey(otherJob);
    for (const freq of ['weekly', 'biweekly', 'monthly']) {
      const periodKey = periodKeyFor(otherDate, freq);
      const current = earliestByPeriod[freq].get(periodKey);
      if (!current || orderKey < current.orderKey) {
        earliestByPeriod[freq].set(periodKey, { orderKey, jobId: otherJob.id });
      }
    }
  }

  return items.filter((item) => {
    const freq = item.frequency || 'daily';
    if (freq === 'daily') return true;
    const periodMap = earliestByPeriod[freq];
    if (!periodMap) return true;
    const anchor = periodMap.get(periodKeyFor(jobDate, freq));
    return !!anchor && anchor.jobId === job.id;
  });
}
